import { Temporal, inTimezone, formatTime } from '@shared/lib/date-time';

export type ShiftClosingReminderStage = 'critical' | 'overdue' | 'soon';

export interface ShiftClosingReminder {
  description: string;
  dismissible: boolean;
  stage: ShiftClosingReminderStage;
  title: string;
  tone: 'danger' | 'warning';
}

export interface ShiftClosingReminderInput {
  closesAt: string | null;
  dismissedAt: string | null;
  lastOrderAt: string | null;
  now: Temporal.Instant;
  shiftOpen: boolean;
  /** Зона клуба: и час закрытия, и «сегодня» для скрытого напоминания считаются по часам локации. */
  zone: string;
}

export const fiscalShiftLastOrderStorageKey = (locationId: string): string =>
  `mors:fiscal-shift-last-sale:${locationId}`;

export function shiftClosingReminder(input: ShiftClosingReminderInput): ShiftClosingReminder | null {
  if (!input.shiftOpen || input.closesAt === null) return null;

  const closesAt = inTimezone(input.closesAt, input.zone);
  const now = inTimezone(input.now, input.zone);

  if (!closesAt || !now) return null;

  const minutesAfterClosing = closesAt.toInstant().until(input.now).total('minutes');
  const closingTime = formatTime(closesAt);

  if (minutesAfterClosing > 30) {
    return {
      description: `Смена открыта уже ${Math.floor(minutesAfterClosing)} минут после закрытия. Если не закрыть сейчас, завтра утром касса не даст принять оплату, пока смену не закроют.`,
      dismissible: false,
      stage: 'critical',
      title: 'Смена осталась открыта',
      tone: 'danger',
    };
  }

  if (minutesAfterClosing >= 0) {
    return {
      description: `Локация закрылась в ${closingTime}, а смена всё ещё открыта. Закройте её перед уходом.`,
      dismissible: false,
      stage: 'overdue',
      title: 'Пора закрыть смену',
      tone: 'warning',
    };
  }

  if (minutesAfterClosing < -30 || isSoonDismissed(input, now)) {
    return null;
  }

  return {
    description: `Локация закрывается в ${closingTime}. Закройте смену после последней оплаты.`,
    dismissible: true,
    stage: 'soon',
    title: 'Скоро закрытие',
    tone: 'warning',
  };
}

function isSoonDismissed(input: ShiftClosingReminderInput, now: Temporal.ZonedDateTime): boolean {
  if (input.dismissedAt === null) return false;

  const dismissedAt = inTimezone(input.dismissedAt, now.timeZoneId);

  if (!dismissedAt || !dismissedAt.toPlainDate().equals(now.toPlainDate())) return false;

  if (input.lastOrderAt === null) return true;

  const lastOrderAt = inTimezone(input.lastOrderAt, now.timeZoneId);

  return !lastOrderAt || Temporal.ZonedDateTime.compare(lastOrderAt, dismissedAt) <= 0;
}
